import React, { useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import API from "../../api/axiosInstance";
import { laDateKeyToUTC } from "../../utils/laDateUtils";
import ParticipantRow from "./ParticipantRow";
import ParticipantPickerToolbar from "./ParticipantPickerToolbar";
import "../css/AllocationParticipantTable.css";

const COL_COUNT = 6;

const ParticipantPickerTable = ({
  people,
  setPeople,
  selectedParticipant,
  onSelectParticipant,
  isParticipantAvailable,
  loadingParticipants,
  selectionLocked = false,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [editingPersonId, setEditingPersonId] = useState(null);
  const [dateSnapshot, setDateSnapshot] = useState(null);
  const [expandedPersonId, setExpandedPersonId] = useState(null);
  const [bookingsCache, setBookingsCache] = useState({});
  const [expandLoading, setExpandLoading] = useState(false);

  const handleExpand = useCallback(async (personId) => {
    if (expandedPersonId === personId) {
      setExpandedPersonId(null);
      return;
    }
    if (bookingsCache[personId]) {
      setExpandedPersonId(personId);
      return;
    }
    setExpandLoading(true);
    setExpandedPersonId(personId);
    try {
      const res = await API.get(`/ems/room-bookings/grouped?participant_id=${personId}`);
      setBookingsCache((prev) => ({ ...prev, [personId]: Array.isArray(res.data) ? res.data : [] }));
    } catch (err) {
      console.error("Failed to fetch grouped bookings:", err);
      setBookingsCache((prev) => ({ ...prev, [personId]: [] }));
    } finally {
      setExpandLoading(false);
    }
  }, [expandedPersonId, bookingsCache]);

  const filtered = people.filter((p) =>
    `${p.fullName} ${p.organisation}`.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleDateChange = (personId, field, value) => {
    setPeople((prev) => prev.map((p) => (p._id === personId ? { ...p, [field]: value } : p)));
  };

  const handleUpdateDates = async (p) => {
    if (p.arrival_la_date_key && p.departure_la_date_key && p.departure_la_date_key < p.arrival_la_date_key) {
      alert(`Invalid dates: Departure cannot be before Arrival for ${p.fullName}`);
      return;
    }
    try {
      const res = await API.get(`/ems/participants/${p.participant_id}`);
      const existing = typeof res.data.person_data === "string"
        ? JSON.parse(res.data.person_data || "{}") : (res.data.person_data || {});

      const updated = { ...existing };
      if (p.arrival_la_date_key) {
        updated.dateTime = laDateKeyToUTC(p.arrival_la_date_key).toISOString();
      }
      if (p.departure_la_date_key) {
        updated.departureDateTime1 = laDateKeyToUTC(p.departure_la_date_key).toISOString();
      }

      await API.put(`/ems/participants/${p.participant_id}`, {
        person_data: JSON.stringify(updated),
      });

      setPeople((prev) =>
        prev.map((px) =>
          px._id === p._id
            ? {
                ...px,
                arrivalDisplay: p.arrival_la_date_key ? laDateKeyToUTC(p.arrival_la_date_key) : null,
                departureDisplay: p.departure_la_date_key ? laDateKeyToUTC(p.departure_la_date_key) : null,
              }
            : px
        )
      );
      setEditingPersonId(null);
      setDateSnapshot(null);
      alert(`Dates updated for ${p.fullName}`);
    } catch (err) {
      console.error("Date update failed:", err);
      alert("Failed to update dates.");
    }
  };

  return (
    <>
      <ParticipantPickerToolbar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        selectionLocked={selectionLocked}
      />

      <div style={{ maxHeight: "420px", overflowY: "auto", marginBottom: "16px" }}>
        <table className="table table-striped table-hover mb-0 allocation-participant-table" style={{ fontSize: "15px" }}>
          <thead style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 1 }}>
            <tr>
              <th style={{ width: "30px" }}></th>
              <th style={{ width: "40px", textAlign: "center" }}></th>
              <th>Name</th>
              <th>Arrival Date</th>
              <th>Departure Date</th>
              <th style={{ width: "130px", textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingParticipants ? (
              <tr>
                <td colSpan={COL_COUNT} style={{ textAlign: "center", padding: "24px", color: "#666" }}>
                  <FontAwesomeIcon icon={faCircleNotch} spin size="lg" />
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={COL_COUNT} style={{ textAlign: "center", color: "#888" }}>
                  No participants match
                </td>
              </tr>
            ) : (
              filtered.map((p) => {
                const available = isParticipantAvailable(p);
                const canSelect = available && !selectionLocked;
                return (
                  <ParticipantRow
                    key={p._id}
                    person={p}
                    selected={selectedParticipant === p._id}
                    onSelect={canSelect ? () => onSelectParticipant(selectedParticipant === p._id ? "" : p._id) : undefined}
                    isAvailable={available}
                    isEditing={editingPersonId === p._id}
                    onEditToggle={() => {
                      if (editingPersonId === p._id) {
                        if (dateSnapshot && dateSnapshot.personId === p._id) {
                          setPeople((prev) =>
                            prev.map((px) =>
                              px._id === p._id
                                ? { ...px, arrival_la_date_key: dateSnapshot.arrival_la_date_key, departure_la_date_key: dateSnapshot.departure_la_date_key }
                                : px
                            )
                          );
                        }
                        setDateSnapshot(null);
                        setEditingPersonId(null);
                      } else {
                        setDateSnapshot({ personId: p._id, arrival_la_date_key: p.arrival_la_date_key, departure_la_date_key: p.departure_la_date_key });
                        setEditingPersonId(p._id);
                      }
                    }}
                    onUpdate={handleUpdateDates}
                    onDateChange={handleDateChange}
                    isExpanded={expandedPersonId === p._id}
                    onExpandToggle={() => handleExpand(p._id)}
                    expandedBookings={bookingsCache[p._id] ?? null}
                    expandLoading={expandLoading && expandedPersonId === p._id}
                    colCount={COL_COUNT}
                    selectionLocked={selectionLocked}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ParticipantPickerTable;
